import { useParams, useLocation } from 'react-router-dom';
import { detokenizeQueryString } from '../../common/utilities/encodeURIComponent';
import useGetSearchResult from '../../hooks/useGetSearchResult';
import {
  Suspense,
  lazy,
  useEffect,
  useReducer,
  useState,
  useMemo,
} from 'react';
import {
  searchInitialState,
  searchReducer,
} from '../../reducers/searchReducer';
import FullScreenLoading from '../../components/FullScreenLoading';
const SearchOverlay = lazy(() => import('../../components/SearchOverlay'));
const DrawerMenu = lazy(() => import('../Home/components/DrawerMenu'));
const FilterModal = lazy(() => import('./components/FilterModal'));
const SearchBar = lazy(() => import('./components/SearchBar'));
const RouteFilters = lazy(() => import('./components/RouteFilters'));
const SearchResults = lazy(() => import('./components/SearchResults'));
import { GetSearchResultQuery } from '../../gql/graphql';
import { Skeleton } from '@mui/material';
import { Helmet } from 'react-helmet';
import Footer from '../../components/Footer/Footer';
import { SearchType } from '../../common/constants/searchType';
import { mainRouteMapping } from './constants/mainRouteMapping';
import { DEFAULT_OG_IMAGE, SITE_ORIGIN } from '../../seo/constants';
import {
  buildCountryCanonical,
  buildMainRouteCanonical,
  getSearchPageMetadata,
  resolveCountryDisplayName,
  slugToDisplayName,
} from '../../seo/metadata';
import {
  buildJsonLdGraph,
  breadcrumbJsonLd,
  itemListJsonLd,
  touristDestinationJsonLd,
  touristTripJsonLd,
} from '../../seo/jsonLd';
import { formatBoatName } from '../../common/utilities/formatBoatName';
import JsonLdScript from '../../seo/JsonLdScript';
import {
  CountryLandingSection,
  RouteLandingSection,
} from './components/EntityLandingSection';

const Search = () => {
  const { countryName, mainRoute } = useParams();
  const location = useLocation();
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [isShowDrawer, setIsShowDrawer] = useState(false);
  const [state, dispatch] = useReducer(searchReducer, searchInitialState);
  const [isShowFilterModal, setIsShowFilterModal] = useState(false);
  const [filteredTrips, setFilteredTrips] = useState<
    GetSearchResultQuery | undefined
  >(undefined);

  // Parse token from URL - this will update when location changes
  const tokenParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('token') || '';
  }, [location.search]);

  const token = useMemo(
    () => detokenizeQueryString(tokenParams),
    [tokenParams],
  );

  // Compute destinationId - updates when location or params change
  const destinationId = useMemo(() => {
    if (tokenParams && token?.destinationId) {
      return isNaN(Number(token.destinationId))
        ? 0
        : Number(token.destinationId);
    }

    if (mainRoute) {
      return mainRouteMapping[mainRoute]?.id || 0;
    }

    if (countryName) {
      const countryMapping: { [key: string]: number } = {
        indonesia: 1,
        maldives: 2,
        thailand: 3,
        myanmar: 4,
        micronesia: 5,
        mexico: 6,
      };

      return (
        countryMapping[
          countryName.toLowerCase() as keyof typeof countryMapping
        ] || 0
      );
    }

    return 0;
  }, [tokenParams, token?.destinationId, mainRoute, countryName]);

  const monthId = useMemo(
    () =>
      tokenParams && token?.monthId
        ? isNaN(Number(token.monthId))
          ? 0
          : Number(token.monthId)
        : 0,
    [tokenParams, token?.monthId],
  );

  const diver = useMemo(
    () =>
      tokenParams && token?.diver
        ? isNaN(Number(token.diver))
          ? 0
          : Number(token.diver)
        : 0,
    [tokenParams, token?.diver],
  );

  const currentYear = useMemo(
    () =>
      tokenParams && token?.currentYear
        ? isNaN(Number(token.currentYear))
          ? 0
          : Number(token.currentYear)
        : 0,
    [tokenParams, token?.currentYear],
  );

  const searchType = useMemo(() => {
    if (mainRoute) {
      return SearchType.MainRoute;
    }
    if (countryName) {
      return SearchType.Country;
    }
    return tokenParams && token?.searchType
      ? Number(token.searchType) || SearchType.AnyDestination
      : SearchType.AnyDestination;
  }, [mainRoute, countryName, tokenParams, token?.searchType]);

  const displayMainRoute = mainRoute
    ? mainRouteMapping[mainRoute]?.name || 'Unknown'
    : '';

  const { searchResult, searchResultLoading } = useGetSearchResult(
    destinationId,
    tokenParams === '' ? 0 : monthId,
    tokenParams === '' ? 1 : diver === 0 ? 1 : diver,
    tokenParams === ''
      ? new Date().getFullYear()
      : currentYear === 0
        ? new Date().getFullYear()
        : currentYear,
    searchType,
  );

  useEffect(() => {
    const destination = mainRoute
      ? displayMainRoute
      : countryName
        ? resolveCountryDisplayName(countryName)
        : (token?.destination ?? 'Anywhere');
    const month = token?.month ?? 'Anytime';
    dispatch({
      type: 'SET_DESTINATION',
      payload: destination.toString(),
    });
    dispatch({
      type: 'SET_MONTH',
      payload: month.toString(),
    });
    dispatch({
      type: 'SET_DIVER',
      payload: isNaN(Number(token?.diver)) ? 1 : Number(token?.diver),
    });
    dispatch({
      type: 'SET_CURRENT_YEAR',
      payload: isNaN(Number(token?.currentYear))
        ? new Date().getFullYear()
        : Number(token?.currentYear),
    });
    dispatch({
      type: 'SET_SEARCH_TYPE',
      payload: isNaN(Number(token?.searchType)) ? 0 : Number(token?.searchType),
    });
    dispatch({
      type: 'SET_DESTINATION_ID',
      payload: isNaN(Number(token?.destinationId))
        ? 0
        : Number(token?.destinationId),
    });
    dispatch({
      type: 'SET_MONTH_ID',
      payload: isNaN(Number(token?.monthId)) ? 0 : Number(token?.monthId),
    });
  }, [
    countryName,
    displayMainRoute,
    mainRoute,
    tokenParams,
    token?.currentYear,
    token?.destination,
    token?.destinationId,
    token?.diver,
    token?.month,
    token?.monthId,
    token?.searchType,
  ]);

  // Reset filtered trips when search parameters change (must run before the setter below)
  useEffect(() => {
    setFilteredTrips(undefined);
  }, [destinationId, monthId, diver, currentYear, searchType]);

  useEffect(() => {
    if (!searchResultLoading) {
      setFilteredTrips(searchResult);
    }
  }, [searchResult, searchResultLoading]);

  const isEntityLanding = Boolean((countryName || mainRoute) && !tokenParams);

  const pageMetadata = getSearchPageMetadata({
    countryName,
    mainRoute,
  });
  const {
    title: pageTitle,
    description: pageDescription,
    canonicalUrl,
    robots,
  } = pageMetadata;

  const tripListItems =
    filteredTrips?.tripWithCriteria.flatMap(trip => {
      if (!trip.boatName) {
        return [];
      }

      const boatName = trip.boatName;
      return trip.availabilityDetails.map(detail => ({
        name: `${boatName} — ${detail.routeName}`,
        url: `${SITE_ORIGIN}/boat/${formatBoatName(boatName)}`,
      }));
    }) ?? [];

  const uniqueTripListItems = Array.from(
    new Map(tripListItems.map(item => [item.url, item])).values(),
  ).slice(0, 20);

  const searchJsonLd = buildJsonLdGraph([
    ...(countryName && pageMetadata.robots === 'index, follow'
      ? [
          touristDestinationJsonLd({
            name: pageMetadata.entityName,
            url: canonicalUrl ?? buildCountryCanonical(countryName),
            description: pageDescription,
          }),
        ]
      : []),
    ...(mainRoute && pageMetadata.robots === 'index, follow'
      ? [
          touristTripJsonLd({
            name: pageMetadata.entityName,
            url: canonicalUrl ?? buildMainRouteCanonical(mainRoute),
            description: pageDescription,
          }),
        ]
      : []),
    breadcrumbJsonLd([
      { name: 'Home', url: `${SITE_ORIGIN}/` },
      ...(countryName
        ? [
            {
              name: pageMetadata.entityName || slugToDisplayName(countryName),
              url: canonicalUrl ?? buildCountryCanonical(countryName),
            },
          ]
        : []),
      ...(mainRoute
        ? [
            {
              name: pageMetadata.entityName || slugToDisplayName(mainRoute),
              url: canonicalUrl ?? buildMainRouteCanonical(mainRoute),
            },
          ]
        : []),
    ]),
    ...(uniqueTripListItems.length > 0 && canonicalUrl
      ? [
          itemListJsonLd({
            name: `${pageMetadata.entityName || 'Liveaboard'} trips`,
            url: canonicalUrl,
            items: uniqueTripListItems,
          }),
        ]
      : []),
  ]);

  const seoHelmet = (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={robots} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
      <JsonLdScript json={searchJsonLd} />
    </Helmet>
  );

  if (searchResultLoading) {
    return (
      <>
        {seoHelmet}
        <FullScreenLoading open={true} />
      </>
    );
  }

  const handleFilterButtonClick = (routeIds: number[]) => {
    if (!searchResult) return;

    const newSearchResult: GetSearchResultQuery = JSON.parse(
      JSON.stringify(searchResult),
    );

    // If no routeIds are provided or the first item is 0, show all trips
    if (!routeIds.length || routeIds[0] === 0) {
      setFilteredTrips(newSearchResult);
      return;
    }

    newSearchResult.tripWithCriteria = newSearchResult.tripWithCriteria
      .map(item => {
        item.availabilityDetails = item.availabilityDetails.filter(
          availability => routeIds.includes(availability.routeId),
        );
        return item;
      })
      .filter(item => item.availabilityDetails.length > 0); // Filter out trips without any availability details

    setFilteredTrips(newSearchResult);
  };

  return (
    <>
      {seoHelmet}
      <div>
        <Suspense
          fallback={
            <Skeleton variant="rectangular" width="100%" height={100} />
          }
        >
          <SearchBar
            destination={state.destination}
            month={state.month}
            diver={state.diver}
            setIsShowDrawer={setIsShowDrawer}
            setShowSearchOverlay={setShowSearchOverlay}
            dispatch={dispatch}
            currentYear={state.currentYear}
            searchType={state.searchType}
            monthId={state.monthId}
            destinationId={state.destinationId}
            currentStage={state.currentStage}
          />
        </Suspense>
        <Suspense
          fallback={
            <Skeleton variant="rectangular" width="100%" height={150} />
          }
        >
          <RouteFilters
            handleFilterButtonClick={handleFilterButtonClick}
            setIsShowFilterModal={setIsShowFilterModal}
            searchResult={searchResult}
          />
        </Suspense>
        <div id="trips">
          <Suspense
            fallback={
              <Skeleton variant="rectangular" width="100%" height={400} />
            }
          >
            <SearchResults searchResult={filteredTrips} state={state} />
          </Suspense>
        </div>
        {isEntityLanding && (
          <div className="mx-6 mt-4 mb-2 lg:mx-36">
            <a
              href="#destination-guide"
              className="text-sm font-semibold text-brand-santtai-primary no-underline hover:underline"
            >
              About this destination ↓
            </a>
          </div>
        )}
        <div id="destination-guide">
          {isEntityLanding && countryName && (
            <CountryLandingSection
              countrySlug={countryName.toLowerCase()}
              countryId={destinationId}
              entityName={pageMetadata.entityName}
            />
          )}
          {isEntityLanding && mainRoute && (
            <RouteLandingSection
              mainRouteSlug={mainRoute.toLowerCase()}
              mainRouteId={destinationId}
              entityName={pageMetadata.entityName}
            />
          )}
        </div>
        <Suspense
          fallback={
            <Skeleton variant="rectangular" width="100%" height={150} />
          }
        >
          <SearchOverlay
            showSearchOverlay={showSearchOverlay}
            setShowSearchOverlay={setShowSearchOverlay}
            dispatch={dispatch}
            destination={state.destination}
            month={state.month}
            diver={state.diver}
            currentYear={state.currentYear}
            searchType={state.searchType}
            destinationId={state.destinationId}
            monthId={state.monthId}
          />
        </Suspense>
        <Suspense
          fallback={
            <Skeleton variant="rectangular" width="100%" height={150} />
          }
        >
          <DrawerMenu
            isShowDrawer={isShowDrawer}
            setIsShowDrawer={setIsShowDrawer}
          />
        </Suspense>
        <Suspense
          fallback={
            <Skeleton variant="rectangular" width="100%" height={150} />
          }
        >
          <FilterModal
            open={isShowFilterModal}
            onClose={setIsShowFilterModal}
            searchResult={searchResult}
            setFilteredTrips={setFilteredTrips}
          />
        </Suspense>
        <Footer />
      </div>
    </>
  );
};

export default Search;
